import {
  IconInfoCircleFilled,
  IconCircleCheckFilled,
  IconAlertTriangleFilled,
  IconSquareXFilled,
  IconX,
} from '@tabler/icons-react'
import { CSSTransition } from 'react-transition-group'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from 'utils/classMerge'

import Text from '../text'

const alertVariants = cva(
  'tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-p-4',
  {
    variants: {
      variant: {
        alert: 'tw-bg-blue-50',
        success: 'tw-bg-emerald-50',
        warning: 'tw-bg-amber-50',
        error: 'tw-bg-rose-50',
      },
    },
    defaultVariants: {
      variant: 'alert',
    },
  },
)

const alertIconsVariants = cva('tw-h-6 tw-min-h-6 tw-w-6 tw-min-w-6', {
  variants: {
    variant: {
      alert: 'tw-text-blue-500',
      success: 'tw-text-emerald-500',
      warning: 'tw-text-amber-500',
      error: 'tw-text-rose-500',
    },
  },
  defaultVariants: {
    variant: 'alert',
  },
})

const alertIcons = {
  alert: IconInfoCircleFilled,
  success: IconCircleCheckFilled,
  warning: IconAlertTriangleFilled,
  error: IconSquareXFilled,
}

export interface AlertProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof alertVariants> {
  message?: string
  onDetails?: () => void
  open?: boolean
  onClose?: () => void
  className?: string
  button?: {
    text?: string
    icon?: string
    className?: string
  }
}

const Alert = ({
  open = true,
  onClose,
  variant,
  message,
  onDetails,
  children,
  className,
  button,
}: AlertProps) => {
  const AlertIcon = alertIcons[variant ?? 'alert']

  return (
    <CSSTransition in={open} timeout={300} classNames="fade" unmountOnExit>
      <div
        role="alert"
        aria-live="assertive"
        className={cn(alertVariants({ variant }), className)}
      >
        <div className="tw-flex tw-grow tw-items-center tw-gap-3">
          <AlertIcon
            data-testid={`alert-icon-${variant}`}
            className={cn(alertIconsVariants({ variant }))}
          />
          {message && (
            <Text className="tw-m-0 tw-text-sm tw-font-medium">{message}</Text>
          )}
          {children && children}
        </div>
        {onDetails && (
          <button
            type="button"
            className={cn(
              'tw-flex tw-items-center tw-gap-1 tw-border-0 tw-border-b tw-border-zinc-600 tw-border-opacity-25 tw-bg-transparent tw-p-0 tw-pb-0.5 tw-capitalize tw-text-zinc-600',
              button?.className || '',
            )}
            onClick={onDetails}
          >
            {button?.icon && button?.icon}
            <Text className="tw-m-0 tw-text-sm tw-font-medium">
              {button?.text || 'view_details'}
            </Text>
          </button>
        )}
        {onClose && (
          <IconX
            data-testid="alert-icon-close"
            onClick={onClose}
            className="tw-h-5 tw-min-h-5 tw-w-5 tw-min-w-5 tw-cursor-pointer"
          />
        )}
      </div>
    </CSSTransition>
  )
}

export { Alert }
